import apiClient from './axios';
import { ApiResponse } from '../types';
import { MaybeApproval } from './approvals';

export type PayrollEmployeeStatus = 'active' | 'inactive';

export type PayrollRunStatus =
  | 'draft'
  | 'pending_approval'
  | 'processing'
  | 'completed'
  | 'partially_failed'
  | 'failed'
  | 'cancelled';

export type PayrollItemStatus = 'pending' | 'processing' | 'paid' | 'failed';

export interface PayrollEmployee {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  employeeNumber: string | null;
  jobTitle: string | null;
  department: string | null;
  startDate: string | null;
  endDate: string | null;
  monthlySalary: number;
  /** Salary components (sum to monthlySalary). */
  basic: number;
  housing: number;
  transport: number;
  other: number;
  /** Annual rent declared, the base for NTA 2025 rent relief. Null means none
   *  on file, which means no relief — it can't be inferred from salary. */
  annualRent: number | null;
  status: PayrollEmployeeStatus;
  /** Set when this staff member is also an admin login. */
  adminUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SalaryComponents {
  basic: number;
  housing: number;
  transport: number;
  other: number;
  /** Annual rent, for the rent-relief term of the preview. */
  annualRent?: number;
}

/** Result of the net-pay preview / the per-item snapshot shape. */
export interface StatutoryBreakdown {
  gross: number;
  pensionEmployee: number;
  pensionEmployer: number;
  nhf: number;
  paye: number;
  /** Annual rent relief applied before the PAYE bands. */
  rentRelief: number;
  totalDeductions: number;
  net: number;
}

export interface PayeBand {
  upTo: number | null;
  rate: number;
}

export interface PayrollTaxConfig {
  id: string;
  pensionEnabled: boolean;
  pensionEmployeePercent: number;
  pensionEmployerPercent: number;
  nhfEnabled: boolean;
  nhfPercent: number;
  /** Rent relief (Nigeria Tax Act 2025) — the Consolidated Relief Allowance
   *  this replaced was repealed with effect from 1 January 2026. */
  rentReliefPercent: number;
  rentReliefAnnualCap: number;
  taxFreeAnnualThreshold: number;
  minimumTaxPercent: number;
  bands: PayeBand[];
  defaultBasicPercent: number;
  defaultHousingPercent: number;
  defaultTransportPercent: number;
  defaultOtherPercent: number;
  updatedByEmail: string | null;
  updatedAt: string;
}

export type UpdateTaxConfigDto = Partial<
  Omit<PayrollTaxConfig, 'id' | 'updatedByEmail' | 'updatedAt'>
>;

export interface EmployeeHistoryRow {
  runId: string;
  runLabel: string;
  periodYear: number;
  periodMonth: number;
  gross: number;
  paye: number;
  pensionEmployee: number;
  nhf: number;
  totalDeductions: number;
  netAmount: number;
  prorated: boolean;
  status: PayrollItemStatus;
  paidAt: string | null;
}

export interface EmployeeHistory {
  employee: PayrollEmployee;
  ytd: {
    year: number;
    count: number;
    gross: number;
    paye: number;
    pensionEmployee: number;
    nhf: number;
    net: number;
  };
  history: EmployeeHistoryRow[];
}

export interface PayrollSettings {
  id: string;
  autoRunEnabled: boolean;
  autoRunDayOfMonth: number;
  updatedByEmail: string | null;
  updatedAt: string;
}

export interface ImportResult {
  created: number;
  errors: { row: number; message: string }[];
}

export interface RemittanceReport {
  year: number;
  month: number;
  label: string;
  headcount: number;
  gross: number;
  net: number;
  paye: number;
  pensionEmployee: number;
  pensionEmployer: number;
  pensionTotal: number;
  nhf: number;
  lines: {
    employeeName: string;
    gross: number;
    paye: number;
    pensionEmployee: number;
    pensionEmployer: number;
    nhf: number;
    net: number;
  }[];
}

export interface PayrollItem {
  id: string;
  payrollRunId: string;
  employeeId: string | null;
  employeeName: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  /** Gross salary. */
  amount: number;
  basic: number;
  housing: number;
  transport: number;
  pensionEmployee: number;
  pensionEmployer: number;
  nhf: number;
  paye: number;
  /** Annual rent relief applied when this item's PAYE was computed. */
  rentRelief: number;
  totalDeductions: number;
  /** What is actually transferred (gross − withheld). */
  netAmount: number;
  /** True when this month was prorated (mid-period joiner/leaver). */
  prorated: boolean;
  status: PayrollItemStatus;
  reference: string;
  providerReference: string | null;
  failureReason: string | null;
  paidAt: string | null;
  /** When the payslip email was sent (null = not sent / no email on file). */
  payslipSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PayrollRun {
  id: string;
  label: string;
  periodMonth: number;
  periodYear: number;
  status: PayrollRunStatus;
  /** Total gross. */
  totalAmount: number;
  totalDeductions: number;
  totalNet: number;
  itemCount: number;
  createdById: string | null;
  createdByEmail: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PayrollRunDetail extends PayrollRun {
  items: PayrollItem[];
}

/** Summary the pay executor returns when a run is paid inline (maker-checker
 *  off). When on, `submitRun` resolves to the approvals envelope instead. */
export interface PayrollPaySummary {
  runId: string;
  total: number;
  paid: number;
  processing: number;
  failed: number;
}

export interface PayrollBank {
  bankName: string;
  bankCode: string;
  logoUrl: string | null;
}

export interface VerifyAccountResult {
  accountName: string;
  accountNumber: string;
  bankCode: string;
  bankName: string;
}

export interface LinkableAdmin {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface CreateEmployeeDto {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  employeeNumber?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  /** Provide either monthlySalary (auto-split) or the components. */
  monthlySalary?: number;
  basic?: number;
  housing?: number;
  transport?: number;
  other?: number;
  annualRent?: number | null;
  adminUserId?: string | null;
  status?: PayrollEmployeeStatus;
}

export type UpdateEmployeeDto = Partial<CreateEmployeeDto>;

export interface CreateRunDto {
  periodMonth: number;
  periodYear: number;
  label?: string;
  employeeIds?: string[];
  notes?: string;
}

export interface ListRunsResult {
  items: PayrollRun[];
  total: number;
  page: number;
  limit: number;
}

export const payrollApi = {
  // ── Employees ──
  listEmployees: async (
    opts: { status?: PayrollEmployeeStatus; q?: string } = {},
  ) => {
    const params: Record<string, string> = {};
    if (opts.status) params.status = opts.status;
    if (opts.q) params.q = opts.q;
    const { data } = await apiClient.get<ApiResponse<PayrollEmployee[]>>(
      '/admin/payroll/employees',
      { params },
    );
    return data.data;
  },

  getEmployee: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<PayrollEmployee>>(
      `/admin/payroll/employees/${id}`,
    );
    return data.data;
  },

  getEmployeeHistory: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<EmployeeHistory>>(
      `/admin/payroll/employees/${id}/history`,
    );
    return data.data;
  },

  getRemittance: async (year: number, month: number) => {
    const { data } = await apiClient.get<ApiResponse<RemittanceReport>>(
      '/admin/payroll/reports/remittance',
      { params: { year, month } },
    );
    return data.data;
  },

  importEmployees: async (rows: CreateEmployeeDto[]) => {
    const { data } = await apiClient.post<ApiResponse<ImportResult>>(
      '/admin/payroll/employees/import',
      { rows },
    );
    return data.data;
  },

  getSettings: async () => {
    const { data } = await apiClient.get<ApiResponse<PayrollSettings>>(
      '/admin/payroll/settings',
    );
    return data.data;
  },

  updateSettings: async (
    patch: Partial<Pick<PayrollSettings, 'autoRunEnabled' | 'autoRunDayOfMonth'>>,
  ) => {
    const { data } = await apiClient.put<ApiResponse<PayrollSettings>>(
      '/admin/payroll/settings',
      patch,
    );
    return data.data;
  },

  createEmployee: async (dto: CreateEmployeeDto) => {
    const { data } = await apiClient.post<ApiResponse<PayrollEmployee>>(
      '/admin/payroll/employees',
      dto,
    );
    return data.data;
  },

  updateEmployee: async (id: string, patch: UpdateEmployeeDto) => {
    const { data } = await apiClient.patch<ApiResponse<PayrollEmployee>>(
      `/admin/payroll/employees/${id}`,
      patch,
    );
    return data.data;
  },

  deleteEmployee: async (id: string) => {
    const { data } = await apiClient.delete<ApiResponse<{ deleted: boolean }>>(
      `/admin/payroll/employees/${id}`,
    );
    return data.data;
  },

  listLinkableAdmins: async () => {
    const { data } = await apiClient.get<ApiResponse<LinkableAdmin[]>>(
      '/admin/payroll/admin-users/linkable',
    );
    return data.data;
  },

  // ── Bank helpers ──
  getBanks: async () => {
    const { data } = await apiClient.get<ApiResponse<PayrollBank[]>>(
      '/admin/payroll/banks',
    );
    return data.data;
  },

  verifyAccount: async (accountNumber: string, bankCode: string) => {
    const { data } = await apiClient.post<ApiResponse<VerifyAccountResult>>(
      '/admin/payroll/verify-account',
      { accountNumber, bankCode },
    );
    return data.data;
  },

  /** Net-pay breakdown for a set of components (drawer preview). */
  previewNet: async (components: SalaryComponents) => {
    const { data } = await apiClient.post<ApiResponse<StatutoryBreakdown>>(
      '/admin/payroll/employees/preview-net',
      components,
    );
    return data.data;
  },

  // ── Tax config ──
  getTaxConfig: async () => {
    const { data } = await apiClient.get<ApiResponse<PayrollTaxConfig>>(
      '/admin/payroll/tax-config',
    );
    return data.data;
  },

  updateTaxConfig: async (patch: UpdateTaxConfigDto) => {
    const { data } = await apiClient.put<ApiResponse<PayrollTaxConfig>>(
      '/admin/payroll/tax-config',
      patch,
    );
    return data.data;
  },

  // ── Runs ──
  listRuns: async (
    opts: { status?: PayrollRunStatus; page?: number; limit?: number } = {},
  ) => {
    const params: Record<string, string | number> = {};
    if (opts.status) params.status = opts.status;
    if (opts.page) params.page = opts.page;
    if (opts.limit) params.limit = opts.limit;
    const { data } = await apiClient.get<ApiResponse<ListRunsResult>>(
      '/admin/payroll/runs',
      { params },
    );
    return data.data;
  },

  getRun: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<PayrollRunDetail>>(
      `/admin/payroll/runs/${id}`,
    );
    return data.data;
  },

  createRun: async (dto: CreateRunDto) => {
    const { data } = await apiClient.post<ApiResponse<PayrollRunDetail>>(
      '/admin/payroll/runs',
      dto,
    );
    return data.data;
  },

  updateRunItem: async (runId: string, itemId: string, amount: number) => {
    const { data } = await apiClient.patch<ApiResponse<PayrollRunDetail>>(
      `/admin/payroll/runs/${runId}/items/${itemId}`,
      { amount },
    );
    return data.data;
  },

  addRunItem: async (runId: string, employeeId: string) => {
    const { data } = await apiClient.post<ApiResponse<PayrollRunDetail>>(
      `/admin/payroll/runs/${runId}/items`,
      { employeeId },
    );
    return data.data;
  },

  removeRunItem: async (runId: string, itemId: string) => {
    const { data } = await apiClient.delete<ApiResponse<PayrollRunDetail>>(
      `/admin/payroll/runs/${runId}/items/${itemId}`,
    );
    return data.data;
  },

  cancelRun: async (id: string) => {
    const { data } = await apiClient.post<ApiResponse<PayrollRunDetail>>(
      `/admin/payroll/runs/${id}/cancel`,
    );
    return data.data;
  },

  /** Submit a draft run for payment. Resolves to the approvals envelope when
   *  maker-checker parks it (the common case), or the pay summary when it
   *  executes inline. Branch with `isApprovalRequired`. */
  submitRun: async (id: string) => {
    const { data } = await apiClient.post<
      ApiResponse<MaybeApproval<PayrollPaySummary>>
    >(`/admin/payroll/runs/${id}/submit`);
    return data.data;
  },

  /** Re-attempt the failed payments in a run (no fresh approval needed). */
  retryFailed: async (id: string) => {
    const { data } = await apiClient.post<ApiResponse<PayrollRunDetail>>(
      `/admin/payroll/runs/${id}/retry-failed`,
    );
    return data.data;
  },

  /** Ops override: confirm a stuck processing item actually landed. */
  markItemPaid: async (runId: string, itemId: string) => {
    const { data } = await apiClient.post<ApiResponse<PayrollRunDetail>>(
      `/admin/payroll/runs/${runId}/items/${itemId}/mark-paid`,
    );
    return data.data;
  },

  /** Ops override: mark a stuck processing item failed (so it can be retried). */
  markItemFailed: async (runId: string, itemId: string, reason?: string) => {
    const { data } = await apiClient.post<ApiResponse<PayrollRunDetail>>(
      `/admin/payroll/runs/${runId}/items/${itemId}/mark-failed`,
      reason ? { reason } : {},
    );
    return data.data;
  },

  resendPayslip: async (runId: string, itemId: string) => {
    const { data } = await apiClient.post<ApiResponse<PayrollRunDetail>>(
      `/admin/payroll/runs/${runId}/items/${itemId}/resend-payslip`,
    );
    return data.data;
  },
};
